import {
  Box,
  chakra,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Radio,
  RadioGroup,
  Stack, Text,
  Tooltip,
  useBreakpointValue
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { nonNumericSymbols, translateSubElementString } from "../../../../../utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EsgLabel, FormInput, FormSelect, NumericFormInput } from "../../../../../components/Forms";
import * as React from "react";
import { FaQuestionCircle } from "react-icons/fa";
import { Answer } from "../../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";
import { areArraysEqual } from "../../../helpers";
import { DATA_CALCULATION_METHODS } from "../helpers";
import { VALIDATION_PATTERNS } from "../../../../Decarbonization/helpers";

function LocationDetails({
                           currentElement,
                           tabKey,
                           callMethod,
                           isPreview
                         }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaire.location.locationDetails"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const formId = `navForm${tabKey}`;
  const currentLocation = currentElement;

  const [emissionsInclusions, setEmissionsInclusions] = useState([]);
  const [rawMaterialsImported, setRawMaterialsImported] = useState("false");
  const [haveSpecificEmissionsData, setHaveSpecificEmissionsData] = useState("false");

  const [isDirty, setIsDirty] = useState(false);

  const rawMaterialsFields = ["description", "quantity", "weight", "unit", "totalCo2EmissionsPerMaterial",
    "rawMaterialsTotalCo2EmissionsEstimated", "rawMaterialsDataVerified", "calculationsMethodology"];

  const locationFormDefaultValues = {
    id: currentLocation.id,
    locationName: currentLocation.locationName,
    totalCo2Emissions: currentLocation.totalCo2Emissions,
    totalCo2EmissionsEstimated: currentLocation.totalCo2EmissionsEstimated === true ? "yes" :
      currentLocation.totalCo2EmissionsEstimated === false ? "no" : "",
    dataVerified: currentLocation.dataVerified === true ? "yes" :
      currentLocation.dataVerified === false ? "no" : "",
    dataCalculationMethod: currentLocation.dataCalculationMethod,
    epdTotalEmissions: currentLocation.epdTotalEmissions,

    description: currentLocation.rawMaterials?.description,
    quantity: currentLocation.rawMaterials?.quantity,
    weight: currentLocation.rawMaterials?.weight,
    unit: currentLocation.rawMaterials?.unit,
    totalCo2EmissionsPerMaterial: currentLocation.rawMaterials?.totalCo2EmissionsPerMaterial,
    rawMaterialsTotalCo2EmissionsEstimated: currentLocation.rawMaterials?.totalCo2EmissionsEstimated === true ?
      "yes" : currentLocation.rawMaterials?.totalCo2EmissionsEstimated === false ? "no" : "",
    rawMaterialsDataVerified: currentLocation.rawMaterials?.dataVerified === true ? "yes" :
      currentLocation.rawMaterials?.dataVerified === false ? "no" : "",
    calculationsMethodology: currentLocation.rawMaterials?.calculationsMethodology
  };

  const {
    register,
    handleSubmit,
    formState,
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: locationFormDefaultValues
  });

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      callMethod({ isFormValid: false });
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, isDirty, callMethod]);

  const totalCo2EmissionsEstimated = watch("totalCo2EmissionsEstimated");
  const dataVerified = watch("dataVerified");
  const dataCalculationMethod = watch("dataCalculationMethod");
  const rawMaterialsDescription = watch("rawMaterialsDescription");
  const unitValue = watch("unit");
  const rawMaterialsEstimated = watch("rawMaterialsTotalCo2EmissionsEstimated");
  const rawMaterialsDataVerified = watch("rawMaterialsDataVerified");
  const calculationsMethodology = watch("calculationsMethodology");

  useEffect(() => {
    setEmissionsInclusions(currentLocation.rawMaterials.emissionsInclusions);
    setRawMaterialsImported(currentLocation.rawMaterialsImported === true ? "true" : "false");
    setHaveSpecificEmissionsData(currentLocation.haveSpecificEmissionsData === true ? "true" : "false");
  }, [
    currentLocation.rawMaterials.emissionsInclusions,
    currentLocation.rawMaterialsImported,
    currentLocation.haveSpecificEmissionsData
  ]);

  const onSubmit = (data) => {
    if (!isPreview) {
      const apiData = {
        id: currentLocation.id,
        locationName: currentLocation.locationName,
        totalCo2Emissions: data.totalCo2Emissions,
        totalCo2EmissionsEstimated: data.totalCo2EmissionsEstimated === "yes",
        dataVerified: data.dataVerified === "yes",
        rawMaterialsImported: rawMaterialsImported === "true"
      };

      if (rawMaterialsImported === "true") {
        apiData.haveSpecificEmissionsData = haveSpecificEmissionsData === "true";
        if (haveSpecificEmissionsData === "true") {
          apiData.dataCalculationMethod = data.dataCalculationMethod;
          if (data.dataCalculationMethod === DATA_CALCULATION_METHODS.EPD) {
            apiData.epdTotalEmissions = data.epdTotalEmissions;
            apiData.rawMaterials = null;
          } else {
            apiData.rawMaterials = {
              description: data.description,
              quantity: data.quantity,
              weight: data.weight,
              unit: data.unit,
              totalCo2EmissionsPerMaterial: data.totalCo2EmissionsPerMaterial,
              calculationsMethodology: data.calculationsMethodology,
              totalCo2EmissionsEstimated: data.rawMaterialsTotalCo2EmissionsEstimated === "yes",
              dataVerified: data.rawMaterialsDataVerified === "yes",
              emissionsInclusions: emissionsInclusions
            };
            apiData.epdTotalEmissions = null;
          }
        } else {
          apiData.dataCalculationMethod = null;
          apiData.rawMaterials = null;
          apiData.epdTotalEmissions = null;
        }
      } else {
        apiData.haveSpecificEmissionsData = null;
        apiData.dataCalculationMethod = null;
        apiData.rawMaterials = null;
        apiData.epdTotalEmissions = null;
      }

      if (isDirty || apiData.rawMaterialsImported !== currentLocation.rawMaterialsImported ||
        (apiData.haveSpecificEmissionsData ?? false) !== (currentLocation.haveSpecificEmissionsData ?? false) ||
        !areArraysEqual(apiData.rawMaterials?.emissionsInclusions ?? [], currentLocation.rawMaterials?.emissionsInclusions ?? [])) {
        callMethod(apiData);
      } else {
        callMethod(null);
      }
      reset(data);
    }
  };

  const containerDirection = useBreakpointValue({
    xl: "row",
    base: "column"
  });

  const dataCalculationMethodSelector = register("dataCalculationMethod", {
    required: haveSpecificEmissionsData === "true" ? globalTranslation("errors.required") : false
  });

  return <chakra.form
    id={formId}
    onSubmit={handleSubmit(onSubmit)}
    flexGrow="1"
    overflowY="auto"
    paddingBlockEnd="4"
    paddingInline="2"
    marginInline="-2"
    height="100%"
  >
    <Flex direction="column" gap={5}>
      {isPreview ?
        /* Start of preview part */

        <>
          <Heading size="sm">{t("checkLocationDetails")}</Heading>
          <Flex direction="column" gap={10}>
            <Box>
              <EsgLabel text={translateSubElementString(
                "supplierQuestionnaire.location.locationDetails.inputs.totalEmissionsFromLocation")} />
              <Answer answerText={locationFormDefaultValues.totalCo2Emissions} />
            </Box>
            <Box>
              <EsgLabel text={`${t(`inputs.totalCo2EmissionsEstimated`)} *`} />
              <Answer answerText={locationFormDefaultValues.totalCo2EmissionsEstimated &&
              locationFormDefaultValues.totalCo2EmissionsEstimated !== "" ?
                t(`inputs.${locationFormDefaultValues.totalCo2EmissionsEstimated}`) : ""} />
            </Box>
            <Box>
              <EsgLabel text={`${t(`inputs.dataVerified`)} *`} />
              <Answer answerText={locationFormDefaultValues.dataVerified &&
              locationFormDefaultValues.dataVerified !== "" ?
                t(`inputs.${locationFormDefaultValues.dataVerified}`) : ""} />
            </Box>
            <Box>
              <EsgLabel text={`${t("inputs.importingRawMaterials")} *`} />
              <Answer answerText={rawMaterialsImported === "true" ?
                t(`inputs.yes`) : t(`inputs.no`)} />
            </Box>
            {rawMaterialsImported === "true" &&
              <Box>
                <EsgLabel text={`${t("inputs.specificEmissionsData")} *`} />
                <Answer answerText={haveSpecificEmissionsData === "true" ?
                  t(`inputs.yes`) : t(`inputs.no`)} />
              </Box>
            }
            {haveSpecificEmissionsData === "true" &&
              <>
                <Box>
                  <EsgLabel text={`${t("inputs.howHasEmissionsBeenCalculated")} *`} />
                  <Answer answerText={t(`inputs.${locationFormDefaultValues.dataCalculationMethod}`, {
                    defaultValue: ""
                  })} />
                </Box>
                {locationFormDefaultValues.dataCalculationMethod === DATA_CALCULATION_METHODS.EPD &&
                  <Box>
                    <EsgLabel text={translateSubElementString(
                      "supplierQuestionnaire.location.locationDetails.inputs.totalEmissions")} />
                    <Answer answerText={locationFormDefaultValues.epdTotalEmissions} />
                  </Box>
                }
                {
                  locationFormDefaultValues.dataCalculationMethod === DATA_CALCULATION_METHODS.DIRECT_DATA_COLLECTION &&
                  <>
                    <Heading size="md">
                      {t("inputs.rawMaterialSpecificQuestions")}
                    </Heading>
                    <Heading size="sm">{t("inputs.checkDetails")}</Heading>
                    <Box>
                      <EsgLabel text={`${t(`inputs.rawMaterials.description`)} *`} />
                      <Answer answerText={locationFormDefaultValues.description} />
                    </Box>
                    <Box>
                      <EsgLabel text={`${t(`inputs.rawMaterials.quantity`)} *`} />
                      <Answer answerText={locationFormDefaultValues.quantity} />
                    </Box>
                    <Box>
                      <EsgLabel text={`${t(`inputs.rawMaterials.weight`)} *`} />
                      <Answer answerText={locationFormDefaultValues.weight} />
                    </Box>
                    <Box>
                      <EsgLabel text={`${t(`inputs.rawMaterials.unit`)} *`} />
                      <Answer answerText={locationFormDefaultValues.unit && locationFormDefaultValues.unit !== "" ?
                        globalTranslation(`measures.${locationFormDefaultValues.unit.toLowerCase()}`) : ""} />
                    </Box>
                    <Box>
                      <EsgLabel text={translateSubElementString(
                        "supplierQuestionnaire.location.locationDetails.inputs.rawMaterials.totalEmissionsPerRawMaterial")}
                      />
                      <Answer answerText={locationFormDefaultValues.totalCo2EmissionsPerMaterial} />
                    </Box>
                    <Box>
                      <EsgLabel text={`${t("inputs.rawMaterials.totalEmissionsEstimated")} *`} />
                      <Answer answerText={locationFormDefaultValues.rawMaterialsTotalCo2EmissionsEstimated &&
                      locationFormDefaultValues.rawMaterialsTotalCo2EmissionsEstimated !== "" ?
                        t(`inputs.${locationFormDefaultValues.rawMaterialsTotalCo2EmissionsEstimated}`) : ""} />
                    </Box>
                    <Box>
                      <EsgLabel text={`${t(`inputs.rawMaterials.dataVerified`)} *`} />
                      <Answer answerText={locationFormDefaultValues.rawMaterialsDataVerified &&
                      locationFormDefaultValues.rawMaterialsDataVerified !== "" ?
                        t(`inputs.${locationFormDefaultValues.rawMaterialsDataVerified}`) : ""} />
                    </Box>
                    <Box>
                      <EsgLabel text={`${t(`inputs.rawMaterials.calculationsMethodology`)} *`} />
                      <Answer answerText={locationFormDefaultValues.calculationsMethodology} />
                    </Box>
                    <Box>
                      <EsgLabel text={t("inputs.rawMaterials.emissionsInclusions")} />
                      <Answer answerText={emissionsInclusions && emissionsInclusions.length > 0 ?
                        emissionsInclusions.map(type =>
                          globalTranslation(`supplierQuestionnaire.emissionsInclusionOptions.${type}`)
                        ).join(", ") : ""} />
                    </Box>
                  </>
                }
              </>
            }
          </Flex>
        </>

        /* End of preview part */
        :
        <>
          <Heading size="sm">{t("provideLocationDetails")}</Heading>
          <NumericFormInput
            label={translateSubElementString(
              "supplierQuestionnaire.location.locationDetails.inputs.totalEmissionsFromLocation")}
            errors={formState.errors}
            {...register("totalCo2Emissions", {
              required: globalTranslation("errors.required"),
              pattern: VALIDATION_PATTERNS.POSITIVE_NUMBER
            })}
            onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
            maxInlineSize={"var(--chakra-sizes-lg)"}
          />
          <Flex gap={5} flexDirection={containerDirection}>
            <FormSelect
              label={
                <Flex alignItems="top">
                  <Text title={`${t(`inputs.totalCo2EmissionsEstimated`)} *`}>
                    {`${t("inputs.totalCo2EmissionsEstimated")} *`}
                  </Text>
                  <Tooltip
                    label={t("inputs.emissionsEstimationTooltip")}
                    whiteSpace={"pre-line"}
                    hasArrow
                  >
                    <IconButton
                      aria-label="emissions-estimation-tooltip"
                      type="button"
                      icon={<FaQuestionCircle size="20" />}
                      flexShrink="0"
                      size="sm"
                      opacity="0.25"
                      tabIndex={-1}
                      height={"unset"}
                    />
                  </Tooltip>
                </Flex>
              }
              errors={formState.errors}
              {...register("totalCo2EmissionsEstimated", {
                required: globalTranslation("errors.required")
              })}
              placeholder=" "
              title={totalCo2EmissionsEstimated && totalCo2EmissionsEstimated !== "" ?
                t(`inputs.${totalCo2EmissionsEstimated}`) : ""}
            >
              <option value={"yes"}>{t(`inputs.yes`)}</option>
              <option value={"no"}>{t(`inputs.no`)}</option>
            </FormSelect>
            <FormSelect
              label={
                <Flex alignItems="top">
                  <Text title={`${t(`inputs.dataVerified`)} *`}>
                    {`${t("inputs.dataVerified")} *`}
                  </Text>
                  <Tooltip
                    label={t("inputs.dataVerifiedTooltip")}
                    whiteSpace={"pre-line"}
                    hasArrow
                  >
                    <IconButton
                      aria-label="data-verified-tooltip"
                      type="button"
                      icon={<FaQuestionCircle size="20" />}
                      flexShrink="0"
                      size="sm"
                      opacity="0.25"
                      tabIndex={-1}
                      height={"unset"}
                    />
                  </Tooltip>
                </Flex>
              }
              errors={formState.errors}
              {...register("dataVerified", {
                required: globalTranslation("errors.required")
              })}
              placeholder=" "
              title={dataVerified && dataVerified !== "" ?
                t(`inputs.${dataVerified}`) : ""}
            >
              <option value={"yes"}>{t(`inputs.yes`)}</option>
              <option value={"no"}>{t(`inputs.no`)}</option>
            </FormSelect>
          </Flex>
          <Box width="50%">
            <FormLabel
              htmlFor={"importingRawMaterialsGroup"}
              opacity="0.75"
              fontSize="xs"
              whiteSpace="initial"
              marginBlockEnd="2"
              blockSize="5"
              height={"max-content"}
              color="esg_gray.200"
              overflow
              title={`${t("inputs.importingRawMaterials")} *`}
            >
              {`${t("inputs.importingRawMaterials")} *`}
            </FormLabel>
            <RadioGroup id={"importingRawMaterialsGroup"} value={rawMaterialsImported} style={{ position: "relative" }}>
              <Stack spacing={5} direction="row">
                <Radio
                  colorScheme="customScheme"
                  value={"true"}
                  onChange={e => {
                    setRawMaterialsImported(e?.target.value);
                  }}
                >
                  {t("inputs.yes")}
                </Radio>
                <Radio
                  colorScheme="customScheme"
                  value={"false"}
                  onChange={e => {
                    setRawMaterialsImported(e?.target.value);
                    setHaveSpecificEmissionsData("false");
                    setEmissionsInclusions([]);
                    rawMaterialsFields.forEach(field => {
                      setValue(field, null);
                    });
                    setValue("dataCalculationMethod", null);
                    setValue("epdTotalEmissions", null);
                  }}
                >
                  {t("inputs.no")}
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>

          {rawMaterialsImported === "true" &&
            <Box>
              <FormLabel
                htmlFor={"specificEmissionsDataGroup"}
                opacity="0.75"
                fontSize="xs"
                whiteSpace="nowrap"
                marginBlockEnd="2"
                blockSize="5"
                isTruncated
                height={"max-content"}
                color="esg_gray.200"
                overflow
                title={`${t("inputs.specificEmissionsData")} *`}
              >
                {`${t("inputs.specificEmissionsData")} *`}
              </FormLabel>
              <RadioGroup id={"specificEmissionsDataGroup"} value={haveSpecificEmissionsData}
                          style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value={"true"}
                    onChange={e => {
                      setHaveSpecificEmissionsData(e?.target.value);
                    }}
                  >
                    {t("inputs.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value={"false"}
                    onChange={e => {
                      setHaveSpecificEmissionsData(e?.target.value);
                      setEmissionsInclusions([]);
                      rawMaterialsFields.forEach(field => {
                        setValue(field, null);
                      });
                      setValue("dataCalculationMethod", null);
                      setValue("epdTotalEmissions", null);
                    }}
                  >
                    {t("inputs.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>
          }

          {haveSpecificEmissionsData === "true" &&
            <>
              <FormSelect
                label={`${t("inputs.howHasEmissionsBeenCalculated")} *`}
                errors={formState.errors}
                {...dataCalculationMethodSelector}
                placeholder=" "
                title={dataCalculationMethod && dataCalculationMethod !== "" ?
                  t(`inputs.${dataCalculationMethod}`) : ""}
                onChange={(e) => {
                  dataCalculationMethodSelector.onChange(e);
                  setEmissionsInclusions([]);
                  rawMaterialsFields.forEach(field => {
                    setValue(field, null);
                  });
                  setValue("epdTotalEmissions", null);
                }}
              >
                <option value={DATA_CALCULATION_METHODS.EPD}>
                  {t(`inputs.${DATA_CALCULATION_METHODS.EPD}`)}
                </option>
                <option value={DATA_CALCULATION_METHODS.DIRECT_DATA_COLLECTION}>
                  {t(`inputs.${DATA_CALCULATION_METHODS.DIRECT_DATA_COLLECTION}`)}
                </option>
              </FormSelect>

              {
                dataCalculationMethod === DATA_CALCULATION_METHODS.EPD && <NumericFormInput
                  label={translateSubElementString(
                    "supplierQuestionnaire.location.locationDetails.inputs.totalEmissions")}
                  errors={formState.errors}
                  {...register("epdTotalEmissions", {
                    required: globalTranslation("errors.required")
                  })}
                  onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
                />
              }

              {
                dataCalculationMethod === DATA_CALCULATION_METHODS.DIRECT_DATA_COLLECTION &&
                <>
                  <Divider marginBlock={2} />
                  <Heading size="md">
                    {t("inputs.rawMaterialSpecificQuestions")}
                  </Heading>
                  <Heading size="sm">{t("inputs.enterDetails")}</Heading>
                  <Flex gap={5} flexDirection={containerDirection}>
                    <FormInput
                      label={`${t(`inputs.rawMaterials.description`)} *`}
                      errors={formState.errors}
                      {...register("description", {
                        required: globalTranslation("errors.required")
                      })}
                      title={rawMaterialsDescription}
                    />
                    <NumericFormInput
                      label={
                        <Flex alignItems="top">
                          <Text title={`${t("inputs.rawMaterials.quantity")} *`}>
                            {`${t("inputs.rawMaterials.quantity")} *`}
                          </Text>
                          <Tooltip
                            label={t("inputs.rawMaterials.quantityTooltip")}
                            whiteSpace={"pre-line"}
                            hasArrow
                          >
                            <IconButton
                              aria-label="location-quantity-tooltip"
                              type="button"
                              icon={<FaQuestionCircle size="20" />}
                              flexShrink="0"
                              size="sm"
                              opacity="0.25"
                              tabIndex={-1}
                              height={"unset"}
                            />
                          </Tooltip>
                        </Flex>
                      }
                      errors={formState.errors}
                      {...register("quantity", {
                        required: globalTranslation("errors.required")
                      })}
                      onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
                    />
                  </Flex>
                  <Flex gap={5} flexDirection={containerDirection}>
                    <NumericFormInput
                      label={`${t("inputs.rawMaterials.weight")} *`}
                      errors={formState.errors}
                      {...register("weight", {
                        required: globalTranslation("errors.required")
                      })}
                      onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
                    />
                    <FormSelect
                      label={`${t("inputs.rawMaterials.unit")} *`}
                      errors={formState.errors}
                      {...register("unit", {
                        required: globalTranslation("errors.required")
                      })}
                      placeholder=" "
                      title={unitValue && unitValue !== "" ? globalTranslation(`measures.${unitValue.toLowerCase()}`) : ""}
                    >
                      {currentLocation.structure?.units?.map(unit => {
                        return <option key={unit} value={unit}>
                          {globalTranslation(`measures.${unit.toLowerCase()}`)}
                        </option>;
                      }) ?? []}
                    </FormSelect>
                  </Flex>
                  <Flex gap={5} flexDirection={containerDirection}>
                    <NumericFormInput
                      label={translateSubElementString(
                        "supplierQuestionnaire.location.locationDetails.inputs.rawMaterials.totalEmissionsPerRawMaterial")}
                      errors={formState.errors}
                      {...register("totalCo2EmissionsPerMaterial", {
                        required: globalTranslation("errors.required")
                      })}
                      onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
                      maxInlineSize="fit-content"
                      maxWidth="100%"
                    />
                  </Flex>
                  <Flex gap={5} flexDirection={containerDirection}>
                    <FormSelect
                      label={
                        <Flex alignItems="top">
                          <Text title={`${t("inputs.rawMaterials.totalEmissionsEstimated")} *`}>
                            {`${t("inputs.rawMaterials.totalEmissionsEstimated")} *`}
                          </Text>
                          <Tooltip
                            label={t("inputs.emissionsEstimationTooltip")}
                            whiteSpace={"pre-line"}
                            hasArrow
                          >
                            <IconButton
                              aria-label="raw-materials-total-emissions-estimated-tooltip"
                              type="button"
                              icon={<FaQuestionCircle size="20" />}
                              flexShrink="0"
                              size="sm"
                              opacity="0.25"
                              tabIndex={-1}
                              height={"unset"}
                            />
                          </Tooltip>
                        </Flex>
                      }
                      errors={formState.errors}
                      {...register("rawMaterialsTotalCo2EmissionsEstimated", {
                        required: globalTranslation("errors.required")
                      })}
                      placeholder=" "
                      title={rawMaterialsEstimated && rawMaterialsEstimated !== "" ?
                        t(`inputs.${rawMaterialsEstimated}`) : ""}
                    >
                      <option value={"yes"}>{t(`inputs.yes`)}</option>
                      <option value={"no"}>{t(`inputs.no`)}</option>
                    </FormSelect>
                    <FormSelect
                      label={
                        <Flex alignItems="top">
                          <Text title={`${t(`inputs.rawMaterials.dataVerified`)} *`}>
                            {`${t("inputs.rawMaterials.dataVerified")} *`}
                          </Text>
                          <Tooltip
                            label={t("inputs.dataVerifiedTooltip")}
                            whiteSpace={"pre-line"}
                            hasArrow
                          >
                            <IconButton
                              aria-label="raw-materials-data-verified-tooltip"
                              type="button"
                              icon={<FaQuestionCircle size="20" />}
                              flexShrink="0"
                              size="sm"
                              opacity="0.25"
                              tabIndex={-1}
                              height={"unset"}
                            />
                          </Tooltip>
                        </Flex>
                      }
                      errors={formState.errors}
                      {...register("rawMaterialsDataVerified", {
                        required: globalTranslation("errors.required")
                      })}
                      placeholder=" "
                      title={rawMaterialsDataVerified && rawMaterialsDataVerified !== "" ?
                        t(`inputs.${rawMaterialsDataVerified}`) : ""}
                    >
                      <option value={"yes"}>{t(`inputs.yes`)}</option>
                      <option value={"no"}>{t(`inputs.no`)}</option>
                    </FormSelect>
                  </Flex>
                  <FormInput
                    label={
                      <Flex alignItems="top">
                        <Text title={`${t(`inputs.rawMaterials.calculationsMethodology`)} *`}>
                          {`${t(`inputs.rawMaterials.calculationsMethodology`)} *`}
                        </Text>
                        <Tooltip
                          label={t("inputs.rawMaterials.calculationsMethodologyTooltip")}
                          whiteSpace={"pre-line"}
                          hasArrow
                        >
                          <IconButton
                            aria-label="raw-materials-calculations-tooltip"
                            type="button"
                            icon={<FaQuestionCircle size="20" />}
                            flexShrink="0"
                            size="sm"
                            opacity="0.25"
                            tabIndex={-1}
                            height={"unset"}
                          />
                        </Tooltip>
                      </Flex>
                    }
                    errors={formState.errors}
                    {...register("calculationsMethodology", {
                      required: globalTranslation("errors.required")
                    })}
                    title={calculationsMethodology}
                    maxInlineSize="fit-content"
                    maxWidth="100%"
                  />
                  <Box>
                    <FormLabel
                      htmlFor={"emissionsInclusionsGroup"}
                      opacity="0.75"
                      fontSize="xs"
                      whiteSpace="nowrap"
                      marginBlockEnd="2"
                      blockSize="5"
                      isTruncated
                      color="esg_gray.200"
                      title={t("inputs.rawMaterials.emissionsInclusions")}
                    >
                      {t("inputs.rawMaterials.emissionsInclusions")}
                    </FormLabel>
                    <CheckboxGroup
                      id={"emissionsInclusionsGroup"}
                      colorScheme="customScheme"
                      value={emissionsInclusions}
                      onChange={(e) => setEmissionsInclusions(e)}
                    >
                      <Checkbox
                        width="max-content" maxWidth="100%"
                        isChecked={emissionsInclusions.length === currentLocation.structure?.emissionsInclusions?.length}
                        onChange={(e) => {
                          if (emissionsInclusions.length === currentLocation.structure?.emissionsInclusions?.length) {
                            setEmissionsInclusions([]);
                          } else {
                            setEmissionsInclusions(currentLocation.structure?.emissionsInclusions ?? []);
                          }
                        }}
                      >
                        {t("inputs.rawMaterials.selectAll")}
                      </Checkbox>
                      <Divider marginBlock={1} width="150px" />
                      <Stack spacing={1} direction={"column"}>
                        {currentLocation.structure?.emissionsInclusions?.map(type => {
                          return <Checkbox width="max-content" maxWidth="100%" key={type} value={type}>
                            {globalTranslation(`supplierQuestionnaire.emissionsInclusionOptions.${type}`)}
                          </Checkbox>;
                        }) ?? null}
                      </Stack>
                    </CheckboxGroup>
                  </Box>
                </>
              }
            </>
          }
        </>
      }

    </Flex>
  </chakra.form>;
}

export default LocationDetails;